import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getCsrfTokenFromCookie } from "core/utils/csrf";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import {
  clearCurrentChats,
  fetchConversationsThunk,
  getMessagesByConvoIdThunk,
} from "modules/flywheel/store/chatStore";
import { useChatbot } from "../../searchAgent/chatBotContext";
import { v4 as uuidv4 } from "uuid";
import { useTheme } from "styled-components";
import {
  IconButton,
  Button,
} from "@mui/material";
import {
  Close,
  OpenInFull,
} from "@mui/icons-material";

import { fetchBaseUrl } from "core/utils/common";
import FullHistoryPanel from "../agenticChatHistory";
import { AuthContext } from "core/contexts/auth/auth";
import { Conversation, Message, Task } from "./types";

// Import our new components
import ConversationSidebar from "./ConversationSidebar";
import MessageList from "./MessageList";
import ChatInput from "./ChatInput";
import TaskSelector from "./TaskSelector";
import { getUserIdFromToken } from "modules/flywheel/utils/incidentUtils";

const baseURL = fetchBaseUrl({ port: 8000 });

// Helper functions for date grouping
const formatDate = (date) => new Date(date);

const isToday = (date) => {
  const today = new Date();

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const isYesterday = (date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  console.log("Conversation date:", date);
  console.log("Yesterday's date:", yesterday);
  console.log("Date comparison:", date.getDate(), yesterday.getDate());

  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

const isPastWeek = (date) => {
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  return date > oneWeekAgo && !isToday(date) && !isYesterday(date);
};

const isPastMonth = (date) => {
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  return date > oneMonthAgo && !isPastWeek(date);
};

const groupConversationsByDate = (conversations: Conversation[]) => {
  const todayConversations: Conversation[] = [];
  const yesterdayConversations: Conversation[] = [];
  const pastWeekConversations: Conversation[] = [];
  const lastMonthConversations: Conversation[] = [];
  const olderConversations: Conversation[] = [];

  conversations.forEach((conversation) => {
    const conversationDate = formatDate(conversation.updated_at);

    if (isToday(conversationDate)) {
      todayConversations.push(conversation);
    } else if (isYesterday(conversationDate)) {
      yesterdayConversations.push(conversation);
    } else if (isPastWeek(conversationDate)) {
      pastWeekConversations.push(conversation);
    } else if (isPastMonth(conversationDate)) {
      lastMonthConversations.push(conversation);
    } else {
      olderConversations.push(conversation);
    }
  });

  return {
    todayConversations,
    yesterdayConversations,
    pastWeekConversations,
    lastMonthConversations,
    olderConversations,
  };
};

// Define the Flywheel-specific task options
const flywheelTasks: Task[] = [
  {
    name: "Chat",
    description: "Ask questions over our agents",
    capabilities: [],
    endpointType: "api",
    endpointsuffix: "serve",
    supports_streaming: true,
  },
  {
    name: "Security Analysis",
    description: "comprehensive security incident analysis and reporting",
    capabilities: [],
    endpointType: "workflow",
    endpointsuffix: "security_analysis",
    supports_streaming: true,
  },
];

const SearchAgentSuite: React.FC = () => {
  const { categorizedConversations } = useAppSelector(
    (state) => state.chatState
  );
  const [fullScreen, setFullScreen] = useState(false);
  const [viewMode, setViewMode] = useState("chat");
  const [searchType, setSearchType] = useState("kb");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [userPrompt, setUserPrompt] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const theme = useTheme();

  // Conversation states
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [todayConversations, setTodayConversations] = useState<Conversation[]>([]);
  const [yesterdayConversations, setYesterdayConversations] = useState<Conversation[]>([]);
  const [pastWeekConversations, setPastWeekConversations] = useState<Conversation[]>([]);
  const [lastMonthConversations, setLastMonthConversations] = useState<Conversation[]>([]);
  const [olderConversations, setOlderConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [highlightedConversation, setHighlightedConversation] = useState("");
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [searchOption, setSearchOption] = useState("Chat");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [hidden, setHidden] = useState<{ [id: string]: boolean }>({
    today: false,
    yesterday: true,
    pastWeek: true,
    lastMonth: true,
    older: true,
  });
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { isVisible, hideChatbot } = useChatbot();
  const dispatch = useAppDispatch();

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const { checkAuthToken } = useContext(AuthContext);

  // Initialize state
  useEffect(() => {
    if (!conversationId) {
      setConversationId(uuidv4());
    }
  }, []);

  // Initialize tasks
  useEffect(() => {
    // Initialize with the first task
    if (flywheelTasks.length > 0) {
      setSelectedTask(flywheelTasks[0]);
      setSearchOption(flywheelTasks[0].name);
    }
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Fetch conversations on component mount
  const handleFetchConversations = useCallback(async () => {
    const amsBearerToken = await checkAuthToken();
    dispatch(fetchConversationsThunk(amsBearerToken));
  }, [dispatch, checkAuthToken]);

  useEffect(() => {
    handleFetchConversations();
    fetchConversations();
  }, [handleFetchConversations]);

  // Handle label clicks (refresh, new, history, etc.)
  const handleLabelClick = useCallback((label) => {
    setSelectedLabel(label === selectedLabel ? null : label);

    // Toggle visibility for date categories
    if (["today", "yesterday", "pastWeek", "lastMonth", "older"].includes(label)) {
      setHidden((prev) => ({
        ...prev,
        [label]: !prev[label],
      }));
    }

    if (label === "refresh") {
      fetchConversations();
      setIsRefreshing(true);
      setTimeout(() => {
        setIsRefreshing(false);
      }, 2000);
      handleFetchConversations();
    }
    
    if (label === "new") {
      setMessages([]);
      setShowFullHistory(false);
      // Create a new conversation ID
      const newConvoId = uuidv4();
      setConversationId(newConvoId);
      dispatch(clearCurrentChats());
      
      // Create a new conversation and add it to Today section
      const newConversation: Conversation = {
        id: newConvoId,
        title: "New Conversation",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_id: null,
        is_active: true,
        message_count: 0,
        metadata: {}
      };
      
      // Add the new conversation to the Today section
      setTodayConversations(prev => [newConversation, ...prev]);
      setSelectedConversation(newConvoId);
    }
    
    if (label === "history") {
      setShowFullHistory(true);
      handleFetchConversations();
    }
  }, [selectedLabel, handleFetchConversations, dispatch]);

  // Fetch conversations from the API
  const fetchConversations = useCallback(async () => {
    try {
      const amsBearerToken = await checkAuthToken();

      const response = await fetch(
        `${baseURL}/socai/api/context/conversations`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": amsBearerToken,
          },
        }
      );

      const data = await response.json();

      console.log("1: Fetched conversations:", data);

      if (data && data.conversations) {
        setConversations(data.conversations);

        console.log("2: Fetched conversations:", data.conversations);

        // Group conversations by date
        const {
          todayConversations,
          yesterdayConversations,
          pastWeekConversations,
          lastMonthConversations,
          olderConversations,
        } = groupConversationsByDate(data.conversations);

        console.log("3: Grouped today's conversations:", {todayConversations})

        setTodayConversations(todayConversations);
        setYesterdayConversations(yesterdayConversations);
        setPastWeekConversations(pastWeekConversations);
        setLastMonthConversations(lastMonthConversations);
        setOlderConversations(olderConversations);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  }, [checkAuthToken]);

  // Fetch messages for a specific conversation
  const fetchConversationMessages = useCallback(async (conversationId: string) => {
    try {
      const amsBearerToken = await checkAuthToken();

      const response = await fetch(
        `${baseURL}/socai/api/conversation/${conversationId}/messages`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": amsBearerToken,
          },
        }
      );
      const data = await response.json();

      // Map them to your Message type
      const backendMessages = (data.messages || []).map((msg: any) => ({
        id: msg.id || `msg-${Date.now()}`,
        type: msg.role === "user" ? "user" : "ai",
        content: msg.content,
        timestamp: new Date(msg.timestamp),
      }));
      setMessages(backendMessages);
    } catch (error) {
      console.error("Error fetching conversation messages:", error);
      setMessages([
        {
          id: `error-${Date.now()}`,
          type: "ai",
          content: "Failed to load messages for this conversation.",
          timestamp: new Date(),
        },
      ]);
    }
  }, [checkAuthToken]);

  // Handle conversation selection
  const handleSelectConversation = useCallback((conversation: Conversation) => {
    setHighlightedConversation(conversation.id);
    setSelectedConversation(conversation.id);
    fetchConversationMessages(conversation.id);
  }, [fetchConversationMessages]);

  // Handle closing the agent
  const handleCloseAgent = useCallback(() => {
    hideChatbot();
  }, [hideChatbot]);

  // Handle file conversion to base64
  const toBase64 = useCallback((file: File): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:*/*;base64, part
        const base64 = (reader.result as string).split(",")[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  }, []);

  // Handle sending message or uploading file
  const postData = useCallback(async () => {
    if (previewFile) {
      setMessages((prev) => [
        ...prev,
        {
          id: `user-file-${Date.now()}`,
          type: "user",
          content: `Uploaded file: ${previewFile.name}`,
          timestamp: new Date(),
        },
      ]);

      try {
        // Add an empty AI message for streaming immediately (this will show the skeleton loader)
        setMessages((prev) => [
          ...prev,
          {
            id: `ai-${Date.now()}`,
            type: "ai",
            content: "",
            timestamp: new Date(),
          },
        ]);
        
        const file_content = await toBase64(previewFile);
        const filePayload = {
          file: {
            file_content,
            file_name: previewFile.name,
            mime_type: previewFile.type,
            file_size: previewFile.size,
          },
          user_input: userPrompt,
          conversation_id: conversationId || uuidv4(),
        };

        const amsBearerToken = await checkAuthToken();
        const csrfToken = getCsrfTokenFromCookie();

        const response = await fetch(
          `${baseURL}/socai/api/upload/orchestrator?stream=true`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-API-Key": amsBearerToken,
              "X-CSRF-Token": csrfToken,
            },
            body: JSON.stringify(filePayload),
          }
        );
        setPreviewFile(null);
        setUserPrompt("");
        if (!response.body) {
          throw new Error("No response body");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let done = false;

        while (!done) {
          const { value, done: doneReading } = await reader.read();
          done = doneReading;
          if (value) {
            const chunk = decoder.decode(value, { stream: true });

            setMessages((prev) => {
              const updated = [...prev];
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].type === "ai") {
                  updated[i] = {
                    ...updated[i],
                    content: updated[i].content + chunk,
                  };
                  break;
                }
              }

              return updated;
            });
          }
        }
      } catch (error) {
        setPreviewFile(null);
        setUserPrompt("");
        console.error("Error:", error);
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          type: "ai",
          content: "Sorry, there was an error processing your file upload.",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } else {
      if (!userPrompt.trim()) return;
      // Add user message to the conversation
      const userMessage: Message = {
        id: `user-${Date.now()}`,
        type: "user",
        content: userPrompt,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      
      // If this is the first message, update the conversation title in Today section
      if (messages.length === 0) {
        setTodayConversations(prev => {
          return prev.map(convo => {
            if (convo.id === conversationId) {
              // Use first ~20 chars of user message as title
              const title = userPrompt.length > 20 ? 
                `${userPrompt.substring(0, 20)}...` : 
                userPrompt;
              return {...convo, title, message_count: 1};
            }
            return convo;
          });
        });
      }

      // Clear input field
      setUserPrompt("");

      // Set loading state
      setIsLoading(true);

      try {
        // Add an empty AI message for streaming immediately (this will show the skeleton loader)
        setMessages((prev) => [
          ...prev,
          {
            id: `ai-${Date.now()}`,
            type: "ai",
            content: "",
            timestamp: new Date(),
          },
        ]);
        
        // Use the correct API endpoint based on the selected task
        let endpoint = `${baseURL}/socai/api/serve`;

        if (selectedTask?.name === "Security Analysis") {
          endpoint = `${baseURL}/socai/api/workflow/security_analysis`;
        }

        const amsBearerToken = await checkAuthToken();
        let userId = await getUserIdFromToken(amsBearerToken);
        const csrfToken = getCsrfTokenFromCookie();

        const response = await fetch(endpoint + "?stream=true&user_id=" + userId, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": amsBearerToken,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            user_input: userMessage.content,
            conversation_id: conversationId,
          }),
        });

        if (!response.body) {
          throw new Error("No response body");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let done = false;

        while (!done) {
          const { value, done: doneReading } = await reader.read();
          done = doneReading;
          if (value) {
            const chunk = decoder.decode(value, { stream: true });
            setMessages((prev) => {
              const updated = [...prev];
              // Find the last AI message (the one we just added)
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].type === "ai") {
                  updated[i] = {
                    ...updated[i],
                    content: updated[i].content + chunk,
                  };
                  break;
                }
              }
              return updated;
            });
          }
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error:", error);

        // Add error message to conversation
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          type: "ai",
          content: "Sorry, there was an error processing your request.",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
        setIsLoading(false);
      }
    }
  }, [previewFile, userPrompt, conversationId, selectedTask, checkAuthToken, toBase64]);

  // Handle data from voice chat
  const handleDataFromChild = useCallback((data: string) => {
    setUserPrompt((prevPrompt) => `${prevPrompt} ${data}`.trim());
  }, []);

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${
        theme.name === "dark-theme"
          ? "backdrop-blur-md bg-gray-700 bg-opacity-10"
          : "backdrop-blur-md bg-purple-300 bg-opacity-40"
      }`}
    >
      <div
        className={`border border-black shadow-lg transition-all duration-300 ease-in-out ${
          fullScreen ? "w-screen h-screen" : "w-[90vw] h-[89vh]"
        } flex`}
      >
        {/* Sidebar */}
        <ConversationSidebar
          theme={theme}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          handleLabelClick={handleLabelClick}
          isRefreshing={isRefreshing}
          setShowFullHistory={setShowFullHistory}
          hidden={hidden}
          todayConversations={todayConversations}
          yesterdayConversations={yesterdayConversations}
          pastWeekConversations={pastWeekConversations}
          lastMonthConversations={lastMonthConversations}
          olderConversations={olderConversations}
          selectedConversation={selectedConversation}
          handleSelectConversation={handleSelectConversation}
          fullScreen={fullScreen}
        />

        {/* Main Content */}
        <div
          className={`flex-1 flex flex-col ${
            theme.name === "dark-theme" ? "bg-[#1d1d1d]" : "bg-[#faf5ff]"
          } overflow-hidden`}
        >
          {/* Header */}
          <div
            className={`flex justify-end ${
              theme.name === "dark-theme"
                ? "bg-[#460073] !important"
                : "bg-[#faf5ff]"
            } px-6 pt-2 pb-2`}
          >
            <div className="space-x-2 flex items-center">
              <IconButton onClick={() => setFullScreen(!fullScreen)}>
                <OpenInFull className="text-purple-600" />
              </IconButton>
              <IconButton onClick={handleCloseAgent}>
                <Close className="text-purple-600" />
              </IconButton>
            </div>
          </div>

          {/* Body */}
          {viewMode === "chat" && (
            <>
              {showFullHistory ? (
                <div className={`flex-1 px-6 py-2 pb-2 space-y-4 overflow-y-auto`}>
                  <FullHistoryPanel
                    isOpen={showFullHistory}
                    onClose={() => setShowFullHistory(false)}
                    conversations={conversations}
                    apptheme={theme}
                    onSelectConversation={fetchConversationMessages}
                    fetchConversations={fetchConversations}
                  />
                </div>
              ) : (
                <>
                  {/* Fixed header section that doesn't scroll */}
                  <TaskSelector
                    filteredTasks={flywheelTasks}
                    selectedTask={selectedTask}
                    setSelectedTask={setSelectedTask}
                    searchOption={searchOption}
                    setSearchOption={setSearchOption}
                    theme={theme}
                  />

                  {/* Scrollable message section */}
                  <div className="flex-1 overflow-y-auto">
                    <div className="p-6 z-1">
                      <MessageList 
                        messages={messages}
                        isLoading={isLoading}
                        theme={theme}
                        messagesEndRef={messagesEndRef}
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Footer */}
          <ChatInput 
            userPrompt={userPrompt}
            setUserPrompt={setUserPrompt}
            postData={postData}
            handleDataFromChild={handleDataFromChild}
            previewFile={previewFile}
            setPreviewFile={setPreviewFile}
            theme={theme}
          />
          
          <div
            className={`text-right mt-2 ${
              theme.name === "dark-theme"
                ? "bg-[#460073] !important"
                : "bg-[#F4EDF9]"
            }`}
          >
            <Button variant="text" color="primary" onClick={handleCloseAgent}>
              Close Agent Suite
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SearchAgentSuite);